import React, { useCallback, useEffect, useMemo } from 'react';
import {
  COMPONENT_MODE,
  COMPONENT_MODE_LABELS,
  ComponentMode,
  FIELD_TYPE,
  FIELD_TYPE_OPTIONS,
  FIELDS,
  SELECT_TYPE,
} from '@/pages/AssetInformationManagement/constant';
import { Button, Form, Input, InputNumber, Modal, Radio, Select } from '@maxtropy/components';
import { SortCustomizeTable } from '@/pages/AssetInformationManagement/components/SortCustomizeTable';
import { Space } from 'antd';

interface Props {
  mode: ComponentMode;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onFinish: (values: any) => void;
  echoValue?: any;
  fieldNames: string[];
  children?: React.ReactNode;
}

export const ModalForm: React.FC<Props> = ({ mode, visible, setVisible, onFinish, echoValue, fieldNames }) => {
  const [form] = Form.useForm();
  const fileType = Form.useWatch(FIELDS.FIELD_TYPE, form);
  const enumList = Form.useWatch(FIELDS.ENUM_ITEM_LIST, form);

  const getFormInitialValues = useCallback(
    () => ({
      [FIELDS.FIELD_NAME]: undefined,
      [FIELDS.FIELD_TYPE]: undefined,
      [FIELDS.IS_REQUIRED]: false,
      [FIELDS.IS_UNIQUE]: false,
      [FIELDS.ENUM_ITEM_LIST]: [],
      [FIELDS.DATE_TIME_FORMAT]: undefined,
      [FIELDS.NUMBER_DECIMAL_PLACES]: 2,
      [FIELDS.SELECT_TYPE]: 1,
    }),
    []
  );

  useEffect(() => {
    if (mode !== COMPONENT_MODE.CREATE) {
      console.log('echovalue合并后values回显', Object.assign(getFormInitialValues(), echoValue));
      form.setFieldsValue(Object.assign(getFormInitialValues(), echoValue));
    }
  }, [echoValue, mode]);

  // 时间格式select
  const dateFormatSelector = useMemo(() => {
    if (fileType !== FIELD_TYPE.DATETIME) return null;
    return (
      <Form.Item
        label="时间格式"
        name={FIELDS.DATE_TIME_FORMAT}
        rules={[{ required: true, message: '请选择时间格式' }]}
      >
        <Select
          disabled={mode !== COMPONENT_MODE.CREATE}
          placeholder="请选择时间格式"
          options={[
            { label: '年-月-日', value: 'yyyy-MM-dd' },
            { label: '年-月', value: 'yyyy-MM' },
            { label: '年', value: 'yyyy' },
          ]}
        />
      </Form.Item>
    );
  }, [fileType, mode]);

  // 小数位数input-number
  const decimalPlacesInput = useMemo(() => {
    if (fileType !== FIELD_TYPE.NUMBER) return null;
    return (
      <Form.Item label="小数位数" name={FIELDS.NUMBER_DECIMAL_PLACES}>
        <InputNumber
          changeOnWheel
          min={0}
          max={8}
          style={{ width: '100%' }}
          step={1}
          precision={0}
          placeholder={'请输入'}
        />
      </Form.Item>
    );
  }, [fileType]);

  const setEnumList = useCallback(
    (list: any[]) => {
      form.setFieldValue(FIELDS.ENUM_ITEM_LIST, [...list]);
      Promise.resolve().then(() => form.validateFields([FIELDS.ENUM_ITEM_LIST]));
    },
    [form]
  );

  const enumTable = useMemo(() => {
    if (fileType !== FIELD_TYPE.ENUM) return null;
    return <SortCustomizeTable enumList={enumList} setEnumList={setEnumList} disabled={mode === COMPONENT_MODE.VIEW} />;
  }, [enumList, setEnumList, fileType, mode]);

  const cancel = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const submit = async () => {
    const data = { ...form.getFieldsValue(), fieldId: echoValue?.fieldId };
    console.log('弹窗确认', data);
    onFinish(data);
    cancel();
  };

  return (
    <Modal
      contentClassName="asset-modal-content"
      title={COMPONENT_MODE_LABELS[mode]}
      open={visible}
      destroyOnClose
      afterClose={() => form.resetFields()}
      footer={() => (
        <Space size={20}>
          <Button onClick={cancel}>{mode !== COMPONENT_MODE.VIEW ? '取消' : '关闭'}</Button>
          {mode !== COMPONENT_MODE.VIEW && (
            <Button type="primary" onClick={() => form.submit()}>
              确定
            </Button>
          )}
        </Space>
      )}
      onCancel={cancel}
    >
      <Form
        form={form}
        initialValues={getFormInitialValues()}
        onFinish={submit}
        layout="horizontal"
        // labelCol={{ span: 8, style: { width: '120px' } }}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        disabled={mode === COMPONENT_MODE.VIEW}
      >
        <Form.Item
          label="字段名称"
          // layout="horizontal"
          name={FIELDS.FIELD_NAME}
          rules={[
            { max: 20, message: '最多输入20个字符' },
            {
              required: true,
              validator: (_, value) => {
                if (!value || !value?.trim()) {
                  return Promise.reject('请输入模版名称');
                }
                if (echoValue && echoValue[FIELDS.FIELD_NAME] === value) {
                  return Promise.resolve();
                }
                return fieldNames.includes(value) ? Promise.reject('字段名称已存在') : Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="请输入字段名称" />
        </Form.Item>
        <Form.Item
          label="字段类型"
          // layout="horizontal"
          name={FIELDS.FIELD_TYPE}
          rules={[{ required: true, message: '请选择字段类型' }]}
        >
          <Select
            // allowClear
            disabled={mode !== COMPONENT_MODE.CREATE}
            placeholder="请输入选择"
            optionFilterProp="label"
            // notFoundContent={loading ? <Spin size="small" /> : null}
            options={FIELD_TYPE_OPTIONS}
          ></Select>
        </Form.Item>
        {/* 动态的时间格式下拉框*/}
        {dateFormatSelector}
        {/* 动态的小数位数输入框*/}
        {decimalPlacesInput}
        {/* 动态的枚举项表格*/}
        {enumTable}

        {/*  基础配置*/}
        <div className="basic-config-title">基础配置</div>
        <Form.Item label={'是否必填'} name={FIELDS.IS_REQUIRED}>
          <Radio.Group
            options={[
              { value: true, label: '是' },
              { value: false, label: '否' },
            ]}
          />
        </Form.Item>
        {fileType === FIELD_TYPE.ENUM && (
          <Form.Item label={'选择类型'} name={FIELDS.SELECT_TYPE}>
            <Radio.Group
              options={[
                { value: SELECT_TYPE.SINGLE, label: '单选' },
                { value: SELECT_TYPE.MULTIPLE, label: '多选' },
              ]}
            />
          </Form.Item>
        )}
        {fileType === FIELD_TYPE.TEXT && (
          <Form.Item label={'是否唯一性校验'} name={FIELDS.IS_UNIQUE}>
            <Radio.Group
              options={[
                { value: true, label: '是' },
                { value: false, label: '否' },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
